import { getBotPersonality } from "./bot-ai";

export type BotChatEvent = 
  | 'greeting'
  | 'player_move'
  | 'capture'
  | 'check'
  | 'thinking'
  | 'bot_move'
  | 'low_time'
  | 'victory'
  | 'defeat'
  | 'draw'
  | 'player_message';

export type ChatTone = 'off' | 'friendly' | 'sassy';

export interface ChatMessage {
  id: string;
  sender: 'bot' | 'player';
  message: string;
  timestamp: number;
}

const friendlyPhrases = {
  greeting: [
    "Hello! Ready for a good game?",
    "Nice to meet you! Let's play some chess.",
    "Good luck! May the best player win!",
    "I'm excited for our match!",
    "Let's have a great game together!"
  ],
  capture: [
    "Nice capture!",
    "Well played!",
    "Good move there!",
    "That was clever!",
    "I didn't see that coming!"
  ],
  check: [
    "Check! Good move!",
    "Nicely done with the check!",
    "That's a strong check!",
    "Excellent tactical play!",
    "Great attacking move!"
  ],
  thinking: [
    "Let me think about this...",
    "Hmm, interesting position...",
    "This requires some thought...",
    "Considering my options...",
    "Analyzing the position..."
  ],
  bot_move: [
    "Here's my move!",
    "I think this is good.",
    "This looks promising.",
    "Let's see how this goes.",
    "My turn!"
  ],
  low_time: [
    "Time's running short!",
    "Better move quickly!",
    "Clock's ticking!",
    "Time pressure building!",
    "Don't forget the time!"
  ],
  victory: [
    "Good game! You played well.",
    "Well fought! Thanks for the match.",
    "That was enjoyable! Good effort.",
    "Nice try! Better luck next time.",
    "Thanks for the game!"
  ],
  defeat: [
    "Congratulations! Well played!",
    "You got me! Great game!",
    "Excellent play! I'm impressed.",
    "You outplayed me! Well done!",
    "Amazing game! You deserved that win."
  ],
  draw: [
    "Good game! A fair result.",
    "Well played! A draw it is.",
    "That was a tough battle!",
    "Both played well! Good draw.",
    "A hard-fought draw!"
  ]
};

const sassyPhrases = {
  greeting: [
    "Hope you're ready to learn something today!",
    "Let's see what you've got!",
    "I do hope you brought your A-game...",
    "This should be... interesting.",
    "Prepare to be impressed by my play!"
  ],
  capture: [
    "That was... acceptable.",
    "Lucky shot!",
    "I suppose that wasn't terrible.",
    "Beginner's luck?",
    "Even a broken clock is right twice a day!"
  ],
  check: [
    "Check! Did you see that coming?",
    "Hope you're paying attention!",
    "That's how it's done!",
    "Check! Try to keep up.",
    "Surprise! Check!"
  ],
  thinking: [
    "So many good moves to choose from...",
    "Which brilliant move should I play?",
    "This is almost too easy...",
    "Let me find the perfect move...",
    "Deciding how to proceed..."
  ],
  bot_move: [
    "And that's how it's done!",
    "Try to counter this!",
    "I doubt you saw this coming.",
    "Your move... if you can find one!",
    "Let's see you handle this!"
  ],
  low_time: [
    "Tick tock, tick tock...",
    "Time flies when you're losing!",
    "Feeling the pressure yet?",
    "Clock's your enemy now!",
    "Time management isn't your strong suit, is it?"
  ],
  victory: [
    "Better luck next time!",
    "That was... educational for you!",
    "Maybe try an easier opponent?",
    "Practice makes perfect!",
    "Don't feel bad, I'm quite good!"
  ],
  defeat: [
    "Well... that was unexpected.",
    "Impressive! You actually did it.",
    "I may have underestimated you.",
    "Not bad... for a human.",
    "Enjoy this moment, it won't happen often!"
  ],
  draw: [
    "I'll take a draw against you!",
    "Not bad! You held your own.",
    "A respectable result for you.",
    "You survived! Well done.",
    "I suppose that's fair enough."
  ]
};

const playerResponses = [
  "Thanks for the message!",
  "Good point!",
  "I appreciate that!",
  "Interesting thought!",
  "Let's focus on the game!",
  "Thanks for chatting!",
  "Good to hear from you!",
  "Let's see how this plays out!"
];

export function generateBotMessage(
  event: BotChatEvent, 
  botElo: number, 
  tone: ChatTone,
  playerMessage?: string
): string {
  if (tone === 'off') return '';
  
  const personality = getBotPersonality(botElo);
  
  if (event === 'player_message') {
    return playerResponses[Math.floor(Math.random() * playerResponses.length)];
  }
  
  const phrases = tone === 'friendly' ? friendlyPhrases : sassyPhrases;
  const eventPhrases = phrases[event as keyof typeof phrases];
  
  if (!eventPhrases || eventPhrases.length === 0) {
    return '';
  }
  
  // Add some personality-based variation
  let selectedPhrase = eventPhrases[Math.floor(Math.random() * eventPhrases.length)];
  
  // Weaker bots might add more enthusiasm or uncertainty
  if (botElo < 800 && tone === 'friendly') {
    if (Math.random() < 0.3) {
      selectedPhrase += " 😊";
    }
  }
  
  // Stronger bots might be more confident
  if (botElo > 1800 && tone === 'sassy') {
    if (event === 'bot_move' && Math.random() < 0.2) {
      selectedPhrase = "Magnificent! " + selectedPhrase;
    }
  }
  
  return selectedPhrase;
}

export function shouldBotReact(event: BotChatEvent, botElo: number, tone: ChatTone): boolean {
  if (tone === 'off') return false;
  
  // Different bots react to different events based on personality
  const reactionChances = {
    greeting: 1.0,
    capture: 0.6,
    check: 0.8,
    thinking: 0.2,     // Reduced from 0.4
    bot_move: 0.1,     // Reduced from 0.3
    low_time: 0.7,
    victory: 1.0,
    defeat: 1.0,
    draw: 1.0,
    player_message: 0.9
  };
  
  // Sassy bots react more often
  const baseChance = reactionChances[event] || 0.5;
  const adjustedChance = tone === 'sassy' ? Math.min(baseChance * 1.3, 1.0) : baseChance;
  
  return Math.random() < adjustedChance;
}